import { useState, useRef, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import assetPath from "../../utils/assetPath";
import { usePoints } from "../../services/dashboard";

const imgCalendarIcon = assetPath("/icons/calender-icon.png");

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

interface DateRange {
  startDate: string;
  endDate: string;
}

interface PointsIssuanceChartProps {
  onApply?: (range: DateRange) => void;
}

const PointsIssuanceChart = ({ onApply }: PointsIssuanceChartProps) => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  const toISO = (d: Date) => d.toISOString().split("T")[0];

  const defaultStart = toISO(thirtyDaysAgo);
  const defaultEnd = toISO(today);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [appliedRange, setAppliedRange] = useState({ start: defaultStart, end: defaultEnd });
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const { data: pointsData, isLoading } = usePoints(appliedRange);

  const chartData = pointsData?.map((d) => ({ name: d.location_name, points: d.points }));

  const handleApply = () => {
    setAppliedRange({ start: startDate, end: endDate });
    onApply?.({ startDate, endDate });
    setPickerOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isLoading && !chartData?.length) return null;

  return (
    <div
      className="bg-white rounded-[8px] p-8 flex flex-col gap-8 w-full"
      style={{ boxShadow: "0px 1px 1px rgba(0,0,0,0.05)" }}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-[10px]">
          <h2 className="text-[18px] font-semibold leading-none text-content-heading">
            Points Issuance by Location
          </h2>
          <p className="text-[14px] font-normal leading-snug text-content-label">
            Comparative analysis of points earned across primary restaurant locations
          </p>
        </div>

        {/* Date range picker */}
        <div ref={pickerRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            className="flex items-center gap-2 px-[13px] py-[7px] rounded-[4px] border border-[#c2c7c9] hover:border-[#023a36] transition-colors bg-surface-input"
          >
            <img src={imgCalendarIcon} alt="" className="w-[10.5px] h-[11.667px]" />
            <span className="text-[12px] font-medium text-content-heading">
              {formatDate(startDate)}
            </span>
            <span className="text-[12px] font-medium text-content-label">—</span>
            <span className="text-[12px] font-medium text-content-heading">
              {formatDate(endDate)}
            </span>
          </button>

          {pickerOpen && (
            <div
              className="absolute right-0 top-full mt-2 z-20 rounded-[8px] border border-[#c2c7c9] p-4 flex flex-col gap-3 min-w-[220px] bg-surface-card"
              style={{ boxShadow: "0px 8px 20px rgba(0,0,0,0.12)" }}
            >
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-content-label">From</label>
                <input
                  type="date"
                  value={startDate}
                  max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-[4px] px-3 py-[7px] text-[12px] font-medium outline-none border border-[#c2c7c9] focus:border-[#023a36] bg-surface-input text-content-heading"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-content-label">To</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-[4px] px-3 py-[7px] text-[12px] font-medium outline-none border border-[#c2c7c9] focus:border-[#023a36] bg-surface-input text-content-heading"
                />
              </div>
              <button
                type="button"
                onClick={handleApply}
                className="w-full rounded-[4px] py-[7px] text-[12px] font-semibold text-white mt-1 bg-brand-primary"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={256}>
        <BarChart data={chartData} barSize={24} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#eeeee5" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 8, fontWeight: 600, fill: "#42484a", fontFamily: "Montserrat, sans-serif" }}
            tickLine={false}
            axisLine={false}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#42484a", fontFamily: "Montserrat, sans-serif" }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(23,48,54,0.05)" }}
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #eeeee5",
              fontFamily: "Montserrat, sans-serif",
              fontSize: 12,
              color: "#011b21",
            }}
            formatter={(value) => [value, "Points"]}
          />
          <Bar dataKey="points" fill="#173036" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PointsIssuanceChart;
